/**
 * ランディングページでマーキー表示するお客様の声（testimonials）。
 *
 * 【表示面の出し分け（#737）】
 * `audience` で2つの面に振り分ける:
 * - `'adult'` … 各セクション文脈に1枚ずつ静置する `LandingVoiceCard`（id 参照）。
 *   45–55歳ベーマガ世代の「今さら」層に「先に始めた同世代がいる＝自分だけじゃない」を効かせる。
 * - `'kid'`   … 「子供だって、きっと楽しい」セクション（`LandingForEducationSection`）の
 *   マーキー（`LandingTestimonialMarquee`）。
 *
 * 【非エンジニアの編集ポイント】
 * 声を追加・修正するときは、このファイルの `testimonials` 配列だけを編集する。
 * コンポーネントを触る必要はない。追加時は `audience` を必ず指定する。
 *
 * 追加時のルール:
 * - `id` は他と重複しない一意な文字列にする（表示順に依存しない安定キー。
 *   Vue の `:key` と、将来の JSON-LD `@id` に使う）。慣例として `ta06` / `tv13` のように連番。
 * - `quote` は本文、`author` は発言者の肩書き／属性。
 * - 実在の顧客の声は、本人の公開利用の許可を得たうえで掲載する。
 *
 * 【将来の JSON-LD Review 出力に向けた設計】
 * 各フィールドは schema.org/Review に無理なくマッピングできる形にしてある:
 * - `quote`      → Review.reviewBody
 * - `author`     → Review.author.name（Person / Organization の name）
 * - `authorRole` → Review.author.jobTitle（任意。現状データは肩書きを `author` に含めるため未使用）
 */
export interface Testimonial {
  /** 表示順に依存しない一意な安定キー。Vue の `:key` と将来の JSON-LD `@id` に使う。 */
  id: string
  /** 声の本文。schema.org/Review の reviewBody に対応。 */
  quote: string
  /** 発言者の属性・肩書き。schema.org/Review の author.name に対応。 */
  author: string
  /** 表示面の出し分け。'adult' = 大人（ベーマガ世代）の声バンド、'kid' = 「子供だって」セクション。 */
  audience: 'adult' | 'kid'
  /**
   * 発言者の役割（任意）。schema.org の author.jobTitle に対応。
   * 現状データは肩書きを `author` に含めているため未使用だが、
   * 氏名と役割を分けたくなった将来のために口を開けてある。
   */
  authorRole?: string
}

export const testimonials: Testimonial[] = [
  // ── 大人（ベーマガ世代）の声。「あそぶ」直後の “声” バンドに表示（#737）。 ──
  // 出典: 購入者第1号・萩原さんインタビュー（2025-11 / 2026-05、Web 掲載許可取得済み）。
  //       言い回しはメモ要旨からの整形（逐語ではない）。
  {
    id: 'ta01',
    quote:
      'デジタルで何でも届く時代に、郵送で物理的な教材が届く。想像以上にワクワクして、「そろそろ届くかな」と発送メールを待っていました。',
    author: '購入者第1号・ベーマガ世代の男性',
    audience: 'adult',
  },
  // 出典: haruka さん（@haruka_apps）フィードバック（2026-04）。作品を最も多く公開している常連クリエイター。
  {
    id: 'ta06',
    quote:
      '教材が届いて、さっそく「はじまりの小屋」を遊んでみたら夢中に。写経は1日1時間まで、と決めないと危ないですね。',
    author: '作品を多数公開するクリエイター',
    audience: 'adult',
  },
  {
    id: 'ta02',
    quote:
      '大人でも楽しく学べて、プログラミングのハードルをぐっと下げてくれる。始めるきっかけとして最適だと思いました。',
    author: '購入者第1号・ベーマガ世代の男性',
    audience: 'adult',
  },
  {
    id: 'ta08',
    quote: '改造も、数値をいじったり色を変えてみたり、楽しいです。',
    author: '作品を多数公開するクリエイター',
    audience: 'adult',
  },
  {
    id: 'ta03',
    quote:
      '魔導書や巻物が実際に手に取れるアイテムのようで、世界観への没入感がすごい。単なる学習じゃなく、知識が身についていく感覚があります。',
    author: '購入者第1号・ベーマガ世代の男性',
    audience: 'adult',
  },
  {
    id: 'ta07',
    quote: '実行しながら、だんだん動いていくところが見られて感動しました。',
    author: '作品を多数公開するクリエイター',
    audience: 'adult',
  },
  {
    id: 'tv12',
    quote: 'デジタルなものを、紙から写経するというのが斬新！',
    author: '20代女性',
    audience: 'adult',
  },

  // ── 子供・保護者・講師の声。「子供だって、きっと楽しい」セクションに表示。 ──
  {
    id: 'tv01',
    quote: 'ゲーム仕立てなので目的をもって取り組みやすい',
    author: 'プログラミング教室講師',
    audience: 'kid',
  },
  {
    id: 'tv02',
    quote:
      'プログラミング教室は高額なので兄弟では厳しいが、子供の可能性はできるだけ広げてやりたい',
    author: '男子小学生兄弟の母',
    audience: 'kid',
  },
  {
    id: 'tv03',
    quote: '（ブラウザだけで良いので）開発環境が整っていていい',
    author: '小学生にプログラミングを教えてみた父',
    audience: 'kid',
  },
  {
    id: 'tv04',
    quote: '魔改造が好きなので、それを楽しめる',
    author: 'プログラミング経験がある小学３年生',
    audience: 'kid',
  },
  {
    id: 'tv05',
    quote: '（他の教材は）まず興味を持ってもらうのが難しいが、これなら',
    author: 'プログラミング教室講師',
    audience: 'kid',
  },
  {
    id: 'tv06',
    quote: 'Scratchは結構できるようになったので、次を探していた',
    author: '小学生男子の父',
    audience: 'kid',
  },
  {
    id: 'tv07',
    quote: 'ＡＩが採点してくれるのがいい',
    author: '小学校高学年の男子',
    audience: 'kid',
  },
  {
    id: 'tv08',
    quote: '見た目が地味なのがいい',
    author: '小学校高学年の男子',
    audience: 'kid',
  },
  {
    id: 'tv09',
    quote: '子供のモチベーションを高められそう',
    author: 'プログラミング教室講師',
    audience: 'kid',
  },
  {
    id: 'tv10',
    quote: 'テキストプログラミングへの移り方を考えていたので、興味に合う',
    author: '小学生男子の父',
    audience: 'kid',
  },
  {
    id: 'tv11',
    quote: '難しそうだけど、打てばできるので大丈夫',
    author: '小学校高学年の男子',
    audience: 'kid',
  },
]
